import React, { useEffect, useState } from 'react';
import { useAppConfig } from '../../context/AppConfigContext';

const MOBILE_BREAKPOINT_PX = 640;

/**
 * Top-of-funnel rotating banner for promos / shipping notes / announcements.
 * Reads `runtime_controls.banner` from app config. Skips the rotation
 * interval when there's only one text. Hidden when `enabled` is false or
 * both text lists are empty.
 */
export const OperationalBanner: React.FC = () => {
  const { config } = useAppConfig();
  const banner = config.runtime_controls?.banner;

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
  });
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Filter out empty strings so admin defaults like ['Envío...', ''] still rotate cleanly.
  const desktopTexts = (banner?.desktop_texts || []).filter((t) => t && t.trim().length > 0);
  const mobileTexts = (banner?.mobile_texts || []).filter((t) => t && t.trim().length > 0);
  const texts = isMobile ? mobileTexts : desktopTexts;
  const rotationMs = Math.max(2000, Math.min(60000, (banner?.rotation_seconds ?? 5) * 1000));

  useEffect(() => {
    if (!banner?.enabled) return;
    if (texts.length <= 1) return;
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % texts.length);
    }, rotationMs);
    return () => window.clearInterval(id);
  }, [banner?.enabled, texts.length, rotationMs]);

  // Reset index if the active list shrinks below current index (e.g. admin
  // removed a text while the banner was rotating).
  useEffect(() => {
    if (index >= texts.length && texts.length > 0) {
      setIndex(0);
    }
  }, [index, texts.length]);

  if (!banner?.enabled) return null;
  if (texts.length === 0) return null;

  const text = texts[index] ?? texts[0];
  const linkLabel = (banner.link_label || '').trim();
  const linkUrl = (banner.link_url || '').trim();
  const showLink = linkLabel.length > 0 && linkUrl.length > 0;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        backgroundColor: banner.background_color || '#008BBC',
        color: banner.text_color || '#FFFFFF',
      }}
      className="w-full px-4 py-2 text-center text-xs sm:text-sm font-poppins"
    >
      <span>{text}</span>
      {showLink && (
        <>
          {' · '}
          <a
            href={linkUrl}
            target={linkUrl.startsWith('http://') || linkUrl.startsWith('https://') ? '_blank' : undefined}
            rel={linkUrl.startsWith('http://') || linkUrl.startsWith('https://') ? 'noopener noreferrer' : undefined}
            className="underline font-medium"
            style={{ color: 'inherit' }}
          >
            {linkLabel}
          </a>
        </>
      )}
    </div>
  );
};
